// =============================================================================
// LobbyView.ts — Comitê de campanha: código da sala com botão de copiar, lista
// de jogadores com status, cartão do candidato selecionado (com a habilidade
// inicial), seletor de dificuldade e o botão de pronto/iniciar.
// =============================================================================
import { CHARACTER_LIST, CHARACTER_TABLE } from '../../../shared/characters';
import { DEFAULT_SKIN, skinDef } from '../../../shared/skins';
import { WEAPON_TABLE } from '../../../shared/weapons';
import { MAX_PLAYERS } from '../../../shared/constants';
import { DIFFICULTIES, type DifficultyId } from '../../../shared/difficulty';
import type { LobbySnapshot } from '../../../shared/protocol';
import { abilityIcon, statIcon } from './pixelIcons';
import { $, charImg, splitNome } from './dom';

// Cor do crachá numerado de cada vaga da sala (1 a 4)
const SLOT_CLASSES = ['n1', 'n2', 'n3', 'n4'];

export class LobbyView {
  private readonly onDifficulty: (id: DifficultyId) => void;
  // Convite de REELEIÇÃO (issue #91): quem não clicou vê quem convocou a chapa.
  // Guarda quem pediu (não o texto): o render monta a frase certa pro host
  // (que só tem INICIAR) e pros demais (que têm PRONTO), e apaga o aviso se
  // quem convocou abandonar o comitê.
  private rematchNotice: { by: string; byId: string } | null = null;

  constructor(handlers: {
    onReadyToggle: () => void;
    onStart: () => void;
    onDifficulty: (id: DifficultyId) => void;
    onBack: () => void;
  }) {
    $('btn-ready').onclick = () => handlers.onReadyToggle();
    $('btn-start').onclick = () => handlers.onStart();
    $('btn-lobby-back').onclick = () => handlers.onBack();
    $('btn-copy-code').onclick = () => void this.copyCode();
    $('lobby-char-count').textContent = `${CHARACTER_LIST.length} CANDIDATOS`;
    this.onDifficulty = handlers.onDifficulty;
  }

  get charGridEl(): HTMLElement { return $('lobby-char-grid'); }

  setReadyState(ready: boolean): void {
    $('btn-ready').textContent = ready ? '✓ PRONTO — CANCELAR' : '🏳️ ESTOU PRONTO';
    $('btn-ready').classList.toggle('ready-on', ready);
  }

  // Aviso de REELEIÇÃO no lugar da dica padrão do comitê (some no próximo jogo)
  setRematchNotice(notice: { by: string; byId: string } | null): void {
    this.rematchNotice = notice;
  }

  // Cartão grande do candidato selecionado: sprite, nome, apelido, descrição
  // e a habilidade especial (a arma inicial que já existe em weapons.ts).
  renderDetail(charId: string, skinId: string = DEFAULT_SKIN): void {
    const ch = CHARACTER_TABLE[charId];
    const box = $('lobby-detail');
    if (!ch) { box.innerHTML = ''; return; }
    const { nome, titulo } = splitNome(ch.nome);
    const wp = WEAPON_TABLE[ch.weapon];
    const skin = skinId !== DEFAULT_SKIN ? skinDef(charId, skinId) : null;
    box.innerHTML = `
      <div class="detalhe-topo">
        <span class="detalhe-sprite">${charImg(ch.id, 96, true, skinId)}</span>
        <div class="detalhe-info">
          <div class="detalhe-nome">${nome}</div>
          <div class="detalhe-titulo">${titulo}</div>
          ${skin ? `<div class="detalhe-skin" title="${skin.desc}">TRAJE: ${skin.nome.toUpperCase()}</div>` : ''}
          <p class="detalhe-desc">${ch.desc}</p>
          <div class="detalhe-stats">
            <span>Nº ${ch.numero}</span>
            <span>${statIcon('hp')} HP ${ch.hp}</span>
            <span>${statIcon('speed')} VEL ${ch.speed}</span>
          </div>
        </div>
      </div>
      <div class="detalhe-habilidade">
        <h3 class="comite-label">HABILIDADE ESPECIAL</h3>
        <div class="habilidade-box">
          <span class="habilidade-icone">${abilityIcon('weapon', ch.weapon, 34)}</span>
          <div>
            <div class="habilidade-nome">${wp?.nome?.toUpperCase() ?? '???'}</div>
            <p class="habilidade-desc">${wp?.desc ?? ''}</p>
          </div>
        </div>
      </div>`;
  }

  render(lobby: LobbySnapshot, youId: string | null): void {
    $('lobby-code').textContent = lobby.code;
    $('lobby-count').textContent = `${lobby.players.length}/${MAX_PLAYERS}`;

    const box = $('lobby-players');
    box.innerHTML = '';
    lobby.players.forEach((p, i) => {
      const ch = CHARACTER_TABLE[p.charId];
      const isHost = p.id === lobby.hostId;
      const row = document.createElement('div');
      const st = !p.connected ? { cls: 'off', txt: 'CAIU', icone: '📴' }
        : p.ready ? { cls: 'ok', txt: 'PRONTO', icone: '✓' }
        : { cls: 'wait', txt: 'ESCOLHENDO...', icone: '⏳' };
      row.className = `lobby-player ${st.cls}${p.id === youId ? ' me' : ''}`;
      row.innerHTML = `
        <span class="num ${SLOT_CLASSES[i] ?? 'n4'}">${i + 1}</span>
        ${charImg(p.charId, 40, true, p.skinId)}
        <span class="quem">
          <b>${p.name}${p.id === youId ? ' (você)' : ''}</b>
          <span class="cand">${ch ? splitNome(ch.nome).nome : '?'}</span>
          <span class="st"><i class="dot"></i>${st.txt}</span>
        </span>
        <span class="icone" title="${isHost ? 'Chefe da chapa' : st.txt}">${isHost ? '👑' : st.icone}</span>`;
      box.appendChild(row);
    });
    // Vagas ociosas: cadeiras vazias esperando a militância chegar
    for (let i = lobby.players.length; i < MAX_PLAYERS; i++) {
      const row = document.createElement('div');
      row.className = 'lobby-player vazio';
      row.innerHTML = `<span class="num">${i + 1}</span><span class="aguarda">+ Aguardando jogador...</span>`;
      box.appendChild(row);
    }

    // Cartão do candidato acompanha o que o servidor sabe do SEU personagem
    const you = lobby.players.find((p) => p.id === youId);
    if (you) this.renderDetail(you.charId, you.skinId);

    const isHost = lobby.hostId === youId;
    this.renderDifficulty(lobby.difficulty, isHost);
    $('btn-start').style.display = isHost ? '' : 'none';
    $('btn-ready').style.display = isHost ? 'none' : '';
    // Se quem convocou a REELEIÇÃO saiu do comitê, o convite perde o objeto
    if (this.rematchNotice && !lobby.players.some((p) => p.id === this.rematchNotice?.byId)) {
      this.rematchNotice = null;
    }
    // O host não tem botão PRONTO (só o INICIAR): o convite instrui cada um
    // pelo botão que ele realmente tem na tela
    const notice = this.rematchNotice
      ? (isHost
        ? `${this.rematchNotice.by} pediu REELEIÇÃO! Quando todos estiverem prontos, solte o INICIAR.`
        : `${this.rematchNotice.by} pediu REELEIÇÃO! Marque PRONTO pra voltar às urnas.`)
      : null;
    $('lobby-hint').textContent = notice ?? (isHost
      ? 'ⓘ Você é o dono da sala. Quando todos estiverem prontos, solte o INICIAR.'
      : 'ⓘ A partida começa quando todos estiverem prontos.');
  }

  // Seletor de dificuldade: o chefe da chapa clica, a militância só assiste.
  private renderDifficulty(selected: DifficultyId, isHost: boolean): void {
    const grid = $('lobby-diff');
    grid.innerHTML = '';
    for (const d of Object.values(DIFFICULTIES)) {
      const card = document.createElement('div');
      card.className = `diff-card diff-${d.id}${d.id === selected ? ' sel' : ''}${isHost ? ' clickable' : ''}`;
      card.innerHTML = `
        <span class="icone">${d.emoji}</span>
        <div class="nome">${d.nome.toUpperCase()}</div>
        <div class="sub">${d.sub}</div>`;
      if (isHost) card.onclick = () => this.onDifficulty(d.id as DifficultyId);
      grid.appendChild(card);
    }
    const sel = DIFFICULTIES[selected];
    $('lobby-diff-desc').textContent =
      `${sel.desc} (vida dos oponentes ×${sel.hpMult}, dano ×${sel.dmgMult})`;
  }

  // Copia o código da sala pro zap da família; feedback rápido no botão
  private async copyCode(): Promise<void> {
    const code = $('lobby-code').textContent ?? '';
    const btn = $('btn-copy-code');
    try {
      await navigator.clipboard.writeText(code);
      btn.textContent = '✓ COPIADO!';
    } catch {
      btn.textContent = code ? 'ANOTA AÍ' : '📋 COPIAR';
    }
    setTimeout(() => { btn.textContent = '📋 COPIAR'; }, 1500);
  }
}
